// DmStream — a DM is a PLACE (spec §1 / §5 rule 8).
//
// One continuous conversation per person. Persistent, scrollable, never resolved.
// The composer is pinned at the bottom. No resolve or assign — fn_thread_assign on
// a DM raises "a DM has no owner — it is a conversation with a person". No
// client-facing marker — a DM is two people, not client-facing by definition.
// Rendered by PERSON (the `with` field), never by id. Paginates backwards with
// p_before (the largest DM is 12,989 messages — never fetch a whole conversation).
import React, { useCallback, useEffect, useState, useRef } from "react";
import { readConversation } from "../../../api/conversations";
import { Composer } from "../kit";
import { EmptyState, ErrorState } from "../../shared/StateMessage";
import PanelErrorBoundary from "../../shared/PanelErrorBoundary";
import MessageRow from "./MessageRow";
import { fmtNumber } from "../../shared/format";
import { useScrollBottom } from "./useScrollBottom";
import { useInfiniteScrollTop } from "./useInfiniteScroll";
import { prependOlder, oldestAt, pageExhausted } from "./mergeMessages";

const PAGE_SIZE = 60;

/**
 * @param {object} props
 * @param {object} props.dm   { id, with, client_id, messages, ... } from the rail's rooms data
 */
export default function DmStream({ dm }) {
  const d = dm || {};
  const [status, setStatus] = useState("loading");
  const [messages, setMessages] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  // Synchronous in-flight guard closed BEFORE the await so a scroll burst can't
  // fire loadMore twice and reuse the same stale cursor (the root cause of the
  // identical-p_before double requests seen in the network trace).
  const loadingMoreRef = useRef(false);

  // Pin the messages scroll container to the bottom on open and after a send.
  const { ref: scrollRef, scrollToBottomAfterPaint } = useScrollBottom();

  const fetchDm = useCallback(async () => {
    const res = await readConversation({ type: "dm", id: d.id, limit: PAGE_SIZE });
    const data = res || {};
    setMessages(Array.isArray(data.messages) ? data.messages : []);
    setNextBefore(data.next_before || null);
    setExhausted(false);
    // The live backend returns the count on `thread.messages`; the fixture uses
    // `dm.messages`. Accept either so production + tests both work.
    const count = typeof data.thread?.messages === "number"
      ? data.thread.messages
      : typeof data.dm?.messages === "number"
        ? data.dm.messages
        : null;
    setTotalCount(count);
    return data;
  }, [d.id]);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      await fetchDm();
      setStatus("ready");
    } catch (err) {
      setStatus("error");
    }
  }, [fetchDm]);

  useEffect(() => { load(); }, [load]);

  // Opening a DM (or switching DMs) jumps to the newest message.
  useEffect(() => {
    if (status !== "ready") return;
    scrollToBottomAfterPaint();
  }, [status, d.id, scrollToBottomAfterPaint]);

  // After a send: re-fetch (no loading skeleton — keeps the reader's place) and
  // jump to the just-sent message. Silent on error so a failed refresh never
  // wipes the list the user is already looking at.
  const refresh = useCallback(async () => {
    try { await fetchDm(); } catch { /* silent */ }
    scrollToBottomAfterPaint();
  }, [fetchDm, scrollToBottomAfterPaint]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    // Cursor: prefer next_before; otherwise the oldest loaded message's `at`.
    const cursor = nextBefore || messages[0]?.at;
    if (!cursor) return;
    // Close the re-entrancy window synchronously, before the await. Without
    // this a scroll burst can fire loadMore twice before the async state
    // update flips the ref; both calls read the same messages[0].at and issue
    // two identical p_before requests (the duplicates in the trace).
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await readConversation({ type: "dm", id: d.id, limit: PAGE_SIZE, before: cursor });
      const data = res || {};
      const older = Array.isArray(data.messages) ? data.messages : [];
      // Dedup by id + sort ascending by `at` so the stream is always monotonic
      // (no out-of-order dates) and boundary refetches can't double a message.
      setMessages((prev) => prependOlder(prev, older));
      setNextBefore(data.next_before || null);
      // The backend omits next_before for DMs. When a page brings nothing
      // strictly older than the cursor we just used, stop paging — otherwise
      // the cursor never advances and loadMore reuses the same p_before
      // forever (the infinite same-cursor refetch in the trace).
      if (pageExhausted(older, cursor)) setExhausted(true);
    } catch (err) { /* silent — first page already loaded */ }
    finally { loadingMoreRef.current = false; setLoadingMore(false); }
  }, [d.id, nextBefore, messages]);

  // hasMore: the backend does NOT return next_before — derive from total count,
  // and honor the `exhausted` flag so a page that brought nothing strictly older
  // (empty page or inclusive boundary) STOPS paging instead of looping forever.
  const hasMore = !exhausted && ((typeof totalCount === "number" && totalCount > messages.length) || !!nextBefore);

  const { sentinelRef } = useInfiniteScrollTop({
    scrollRef,
    hasMore,
    loading: loadingMore,
    ready: status === "ready",
    onLoadMore: loadMore,
  });

  if (status === "loading") {
    return (
      <div className="space-y-2 p-4" role="status" aria-label="Loading">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
        ))}
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="p-4">
        <ErrorState variant="inset" message="This conversation is temporarily unavailable. Please try again shortly." onRetry={load} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid="dm-stream">
      <header className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-800">
        <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden="true" />
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{d.with || "Direct message"}</span>
        {typeof d.messages === "number" && (
          <span className="ml-auto text-xs text-gray-400 tabular-nums">{fmtNumber(d.messages)} messages</span>
        )}
      </header>

      <PanelErrorBoundary label="dm-stream-messages">
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-testid="dm-scroll">
          {hasMore && !loadingMore && (
            <div className="mb-3 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-gray-800"
                data-testid="stream-load-more"
              >
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15" />
                </svg>
                Load older messages
              </button>
            </div>
          )}
          {loadingMore && (
            <div className="mb-3 flex items-center justify-center gap-2" data-testid="stream-loading-more">
              <svg className="h-4 w-4 animate-spin text-[#32B1FF]" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-xs text-gray-500 dark:text-gray-400">Loading older messages…</span>
            </div>
          )}
          {messages.length === 0 ? (
            <EmptyState variant="inset" message="No messages yet." padding="py-8" />
          ) : (
            <div className="space-y-1.5">
              {messages.map((m) => (
                <MessageRow key={m.id} message={m} />
              ))}
            </div>
          )}
        </div>
      </PanelErrorBoundary>

      <div className="shrink-0 border-t border-gray-200 p-3 dark:border-gray-800">
        <Composer channel="chat" isDm threadId={d.id} clientId={d.client_id} onSent={refresh} />
      </div>
    </div>
  );
}